/* eslint-disable unicorn/prefer-ternary */
/* eslint-disable unicorn/prefer-switch */
/* eslint-disable complexity */
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import config from '@common/configs'
import { truncateString } from '@common/functions/truncateString'
import {
    handleMintController as handleMintControllerAlgorand,
    handleWithdrawController as handleWithdrawControllerAlgorand,
} from '@controllers/AlgorandController'
import AlgorandNetwork from '@providers/AlgorandNetwork'
import { THandleSafeMintCallbackObject } from '@providers/models'
import useStoreNetworkWallet, { INetworkWalletsData } from '@store/networkWalletStore'
import { IMetadataProperties, TAssetInfoResult } from '@types'

import useMain from './useMain'
import useNetwork from './useNetwork'

const MAX_TRUNCATE_LENGTH = 14

export default function useNetworkWallet() {
    const {
        state: {
            transactionHash,
            contract,
            mainInit: { network: networkInit, collectionId, projectId },
            selectedSalesBehaviour,
            coupon,
        },
        operations: { setWarningMessage, setShouldWarning, setTransactionHash, setCoupon, setCurrentStep },
    } = useMain()
    const { t } = useTranslation()

    const {
        state: { currentMintPrice, blockchain, contractAddress, currentContractAddress, selectedOption, network },
    } = useNetwork()

    const { networkManager } = useStoreNetworkWallet()

    const walletProvider = useStoreNetworkWallet((state: INetworkWalletsData) => state.walletProvider)
    const setWalletProvider = useStoreNetworkWallet((state: INetworkWalletsData) => state.setWalletProvider)
    const isLoadingContext = useStoreNetworkWallet((state: INetworkWalletsData) => state.isLoadingContext)
    const setIsLoadingContext = useStoreNetworkWallet((state: INetworkWalletsData) => state.setIsLoadingContext)
    const estimatedGasFee = useStoreNetworkWallet((state: INetworkWalletsData) => state.estimatedGasFee)
    const setEstimatedGasFee = useStoreNetworkWallet((state: INetworkWalletsData) => state.setEstimatedGasFee)
    const estimatedTotalCost = useStoreNetworkWallet((state: INetworkWalletsData) => state.estimatedTotalCost)
    const setEstimatedTotalCost = useStoreNetworkWallet((state: INetworkWalletsData) => state.setEstimatedTotalCost)
    const mintResults = useStoreNetworkWallet((state: INetworkWalletsData) => state.mintResults)
    const setMintResults = useStoreNetworkWallet((state: INetworkWalletsData) => state.setMintResults)
    const metadataResults = useStoreNetworkWallet((state: INetworkWalletsData) => state.metadataResults)
    const setMetadataResults = useStoreNetworkWallet((state: INetworkWalletsData) => state.setMetadataResults)
    const balance = useStoreNetworkWallet((state: INetworkWalletsData) => state.balance)
    const setBalance = useStoreNetworkWallet((state: INetworkWalletsData) => state.setBalance)
    const isConnected = useStoreNetworkWallet((state: INetworkWalletsData) => state.isConnected)
    const setIsConnected = useStoreNetworkWallet((state: INetworkWalletsData) => state.setIsConnected)
    const loadingMessage = useStoreNetworkWallet((state: INetworkWalletsData) => state.loadingMessage)
    const setLoadingMessage = useStoreNetworkWallet((state: INetworkWalletsData) => state.setLoadingMessage)
    const suggestedGasFees = useStoreNetworkWallet((state: INetworkWalletsData) => state.suggestedGasFees)
    const setSuggestedGasFees = useStoreNetworkWallet((state: INetworkWalletsData) => state.setSuggestedGasFees)
    const shouldEstimateGas = useStoreNetworkWallet((state: INetworkWalletsData) => state.shouldEstimateGas)
    const setShouldEstimateGas = useStoreNetworkWallet((state: INetworkWalletsData) => state.setShouldEstimateGas)
    const onMintGasFees = useStoreNetworkWallet((state: INetworkWalletsData) => state.onMintGasFees)
    const setOnMintGasFees = useStoreNetworkWallet((state: INetworkWalletsData) => state.setOnMintGasFees)
    const truncatedAddress = useStoreNetworkWallet((state: INetworkWalletsData) => state.truncatedAddress)
    const setTruncatedAddress = useStoreNetworkWallet((state: INetworkWalletsData) => state.setTruncatedAddress)
    const truncatedContractAddress = useStoreNetworkWallet(
        (state: INetworkWalletsData) => state.truncatedContractAddress,
    )
    const setTruncatedContractAddress = useStoreNetworkWallet(
        (state: INetworkWalletsData) => state.setTruncatedContractAddress,
    )
    const currentWalletAddress = useStoreNetworkWallet((state: INetworkWalletsData) => state.currentWalletAddress)
    const setCurrentWalletAddress = useStoreNetworkWallet((state: INetworkWalletsData) => state.setCurrentWalletAddress)
    const reset = useStoreNetworkWallet((state: INetworkWalletsData) => state.reset)

    const updateNetworkManager = useCallback(() => {
        networkManager
            .setBlockchain(blockchain)
            .setNetworkName(networkInit)
            .setContract(contract)
            .setProjectId(projectId)
            .updateProvider()
    }, [blockchain, contract, networkInit, networkManager])

    useEffect(() => {
        updateNetworkManager()
    }, [updateNetworkManager])

    const handleMintResults = React.useCallback(
        (mintResult: TAssetInfoResult[] | Record<string, unknown>) => {
            switch (networkInit) {
                case 'ethereum':
                    setMintResults([...mintResults, mintResult as Record<string, unknown>])
                    break
                case 'polygon':
                    setMintResults([...mintResults, mintResult as Record<string, unknown>])
                    break
                default:
                    setMintResults([...mintResults, ...(mintResult as TAssetInfoResult[])])
            }
        },
        [mintResults],
    )
    const handleMetadataResults = React.useCallback(
        (metadataResult: IMetadataProperties[]) => {
            setMetadataResults([...metadataResults, ...metadataResult])
        },
        [metadataResults],
    )

    const connectEthereumAlgorantWallet = React.useCallback(async () => {
        setLoadingMessage(t('network.wallet.connect'))
        let isWalletConnected = false

        const connectResponse = await networkManager.getConnectedWallet()

        if (connectResponse.success) {
            isWalletConnected = true
            setIsConnected(true)
            setBalance(connectResponse.data.balance)
            setTruncatedAddress(truncateString(connectResponse.data.accounts[0], MAX_TRUNCATE_LENGTH))
            setCurrentWalletAddress(connectResponse.data.accounts[0])
            setLoadingMessage(connectResponse.message)
        } else {
            setBalance('0')
            setTruncatedAddress('')
            setCurrentWalletAddress('')
            setLoadingMessage(connectResponse.error)
        }

        setIsConnected(isWalletConnected)

        return isWalletConnected
    }, [networkManager, setBalance, setIsConnected, setLoadingMessage, t])

    const estimateGasPrice = React.useCallback(async () => {
        setLoadingMessage('network.wallet.estimating')
        const gasFeeResponse = await networkManager.getGasFeePriceEstimate(
            contractAddress[currentContractAddress],
            currentMintPrice,
            selectedSalesBehaviour.position,
            coupon,
            selectedSalesBehaviour.category,
            selectedOption,
            selectedSalesBehaviour.maxPerWallet,
        )

        setLoadingMessage('network.wallet.estimated')

        if (gasFeeResponse.success) {
            setSuggestedGasFees(gasFeeResponse.gasFee)
        } else {
            setWarningMessage(gasFeeResponse.error)
            setShouldWarning(true)
        }
    }, [
        contractAddress,
        coupon,
        currentContractAddress,
        currentMintPrice,
        networkManager,
        selectedOption,
        selectedSalesBehaviour.category,
        selectedSalesBehaviour.maxPerWallet,
        selectedSalesBehaviour.position,
        setLoadingMessage,
        setShouldWarning,
        setSuggestedGasFees,
        setWarningMessage,
    ])

    const handleWhitelist = React.useCallback(async () => {
        setLoadingMessage('network.wallet.whitelist.handling')
        // Define Provider
        if (selectedSalesBehaviour.hasWhiteList) {
            const checkWhiteList = await networkManager.checkWhitelist(collectionId, selectedSalesBehaviour.position)

            if (checkWhiteList.success) {
                setCoupon(checkWhiteList.coupon)
                setLoadingMessage('network.wallet.mint.minting')
                return true
            }

            setWarningMessage('Not in whitelist, try to connect with another wallet')
            setShouldWarning(true)
            return false
        }

        return true
    }, [
        collectionId,
        networkManager,
        selectedSalesBehaviour.hasWhiteList,
        selectedSalesBehaviour.position,
        setCoupon,
        setLoadingMessage,
        setShouldWarning,
        setWarningMessage,
    ])

    const handleWithdraw = React.useCallback(
        async (event: React.SyntheticEvent<HTMLButtonElement>) => {
            setIsLoadingContext(true)
            if (walletProvider) {
                await handleWithdrawControllerAlgorand(
                    metadataResults,
                    walletProvider as AlgorandNetwork,
                    contractAddress[currentContractAddress],
                    Number(event.currentTarget.value),
                    setShouldWarning,
                    setWarningMessage,
                )
            }
            setIsLoadingContext(false)
            window.alert('Transfer complete!')
        },
        [contractAddress, currentContractAddress, metadataResults, setShouldWarning, setWarningMessage, walletProvider],
    )

    const handleMintCallback = useCallback(
        (payload: THandleSafeMintCallbackObject) => {
            console.log('[TESTING CALLBACK]', payload)
            if (payload.type === 'feedback') {
                setLoadingMessage(payload.message)
            } else if (payload.type === 'message') {
                setWarningMessage(payload.message)
                setShouldWarning(true)
            } else if (payload.type === 'gasFee') {
                setOnMintGasFees(payload.value)
            } else if (payload.type === 'hash') {
                setTransactionHash(payload.value)
            } else if (payload.type === 'mintResult') {
                handleMintResults(payload.value)
            } else if (payload.type === 'mintMetadata') {
                handleMetadataResults(payload.value)
            } else if (payload.type === 'sent') {
                setCurrentStep('confirming')
            }
        },
        [
            handleMetadataResults,
            handleMintResults,
            setCurrentStep,
            setLoadingMessage,
            setOnMintGasFees,
            setShouldWarning,
            setTransactionHash,
            setWarningMessage,
        ],
    )

    const handleMint = React.useCallback(async () => {
        const shouldMint = await handleWhitelist()
        let isMinted = false
        if (shouldMint && walletProvider) {
            setLoadingMessage(t('network.wallet.mint.minting'))
            if (networkInit === 'algorand') {
                isMinted = await handleMintControllerAlgorand(
                    balance,
                    currentMintPrice,
                    suggestedGasFees,
                    contractAddress[currentContractAddress],
                    selectedOption,
                    walletProvider as AlgorandNetwork,
                    handleMintResults as (mintResult: TAssetInfoResult[]) => void,
                    handleMetadataResults,
                    setLoadingMessage,
                    setShouldWarning,
                    setWarningMessage,
                )
            } else {
                const mintResponse = await networkManager.mintTransaction(
                    contractAddress[currentContractAddress],
                    currentMintPrice,
                    selectedOption,
                    selectedSalesBehaviour.position,
                    coupon,
                    selectedSalesBehaviour.category,
                    selectedSalesBehaviour.maxPerWallet,
                    handleMintCallback,
                )

                isMinted = mintResponse.success

                if (mintResponse.success) {
                    setCurrentStep('success')
                } else {
                    setCurrentStep('payments')
                    setWarningMessage(mintResponse.error)
                    toast(mintResponse.error)
                }
            }
        }
        isMinted && setIsConnected(false)
        return isMinted
    }, [
        handleWhitelist,
        walletProvider,
        setIsConnected,
        setLoadingMessage,
        t,
        networkInit,
        balance,
        currentMintPrice,
        suggestedGasFees,
        contractAddress,
        currentContractAddress,
        selectedOption,
        handleMintResults,
        handleMetadataResults,
        setShouldWarning,
        setWarningMessage,
        networkManager,
        selectedSalesBehaviour.position,
        selectedSalesBehaviour.category,
        selectedSalesBehaviour.maxPerWallet,
        coupon,
        handleMintCallback,
        setCurrentStep,
    ])

    const newEstimateGas = async () => {
        setShouldEstimateGas(true)
        if (isConnected && (networkInit === 'ethereum' || networkInit === 'polygon')) {
            await estimateGasPrice()
        }
    }
    const handleIntallPlugin = React.useCallback((wallet: string) => {
        window.alert(`Please install ${wallet} plugin, or select another payment method.`)
    }, [])

    const handleSelectPaymentMethod = React.useCallback(
        (event: React.SyntheticEvent<HTMLButtonElement>) => {
            networkManager.setWalletType(event.currentTarget.value).updateProvider()

            let provider

            if (network === 'algorand') {
                provider = networkManager.getProvider(network)
                if (networkManager.getProvider(network).getCurrentWallet() !== event.currentTarget.value) {
                    return handleIntallPlugin(event.currentTarget.value)
                }
            } else if (network === 'polygon' || network === 'ethereum') {
                provider =
                    network === 'polygon'
                        ? networkManager.getProvider('polygon')
                        : networkManager.getProvider('ethereum')

                if (provider.getCurrentWallet() !== event.currentTarget.value) {
                    setWarningMessage(
                        `You don't have ${event.currentTarget.value} extension installed in your browser.`,
                    )
                    return setShouldWarning(true)
                }
            }

            if (!provider) {
                setWarningMessage(`Unknown network ${network}`)
                return setShouldWarning(true)
            }

            setWalletProvider(provider)
        },
        [
            blockchain,
            config.networks.algorand,
            config.networks.ethereum,
            setShouldWarning,
            setWalletProvider,
            setWarningMessage,
            network,
            networkManager,
        ],
    )

    const handleChangeAddress = useCallback(
        async (address: string[]) => {
            const balance = await networkManager.getBalance(address[0])
            setBalance(balance)
            const walletAddress = address[0]
            setCurrentWalletAddress(walletAddress)
            setTruncatedAddress(truncateString(walletAddress, MAX_TRUNCATE_LENGTH))
        },
        [setCurrentWalletAddress],
    )

    return {
        state: {
            isLoadingContext,
            truncatedContractAddress,
            estimatedGasFee,
            estimatedTotalCost,
            onMintGasFees,
            truncatedAddress,
            suggestedGasFees,
            shouldEstimateGas,
            balance,
            currentMintPrice,
            mintResults,
            loadingMessage,
            isConnected,
            walletProvider,
            transactionHash,
            metadataResults,
            currentWalletAddress,
        },
        operations: {
            handleMintResults,
            handleMetadataResults,
            connectEthereumAlgorantWallet,
            handleMint,
            handleWithdrawl: handleWithdraw,
            setWalletProvider,
            handleWhiteList: handleWhitelist,
            setEstimatedGasFee,
            setTruncatedAddress,
            setEstimatedTotalCost,
            setIsLoadingContext,
            setShouldEstimateGas,
            setCurrentWalletAddress,
            reset,
            newEstimateGas,
            setTruncatedContractAddress,
            handleSelectPaymentMethod,
            handleChangeAddress,
            updateNetworkManager,
        },
    }
}
